import React, { useCallback, useContext, useMemo } from 'react';
import { BaseEdge, EdgeLabelRenderer, useReactFlow, getBezierPath } from 'reactflow';
import { WorkflowEdgesContext } from './WorkflowEdgesContext';
import { findInsertionIndex } from './edgeGeometry';
import './DraggableEdge.css';

// Smooth curve through every point (source -> waypoints -> target) using a Catmull-Rom spline
// converted to cubic Bezier segments — unlike a plain bezier approximation, this passes exactly
// through each point, which matters here since a dragged waypoint should be where the line
// actually goes, not just an influence on a curve that passes near it.
function smoothPath(points) {
  if (points.length < 3) {
    return points.map((p, i) => `${i === 0 ? 'M' : 'L'} ${p.x},${p.y}`).join(' ');
  }
  let d = `M ${points[0].x},${points[0].y}`;
  for (let i = 0; i < points.length - 1; i++) {
    const p0 = points[i - 1] || points[i];
    const p1 = points[i];
    const p2 = points[i + 1];
    const p3 = points[i + 2] || p2;
    const cp1x = p1.x + (p2.x - p0.x) / 6;
    const cp1y = p1.y + (p2.y - p0.y) / 6;
    const cp2x = p2.x - (p3.x - p1.x) / 6;
    const cp2y = p2.y - (p3.y - p1.y) / 6;
    d += ` C ${cp1x},${cp1y} ${cp2x},${cp2y} ${p2.x},${p2.y}`;
  }
  return d;
}

// Label position along the curve's actual midpoint (by path length, not by "the middle element
// of the points list") offset perpendicular to the path so the label reads beside the line
// instead of sitting on it. Picking the middle array element used to mean the label rendered at
// EXACTLY a waypoint's own coordinates whenever there was exactly one (and often otherwise too),
// covering the "True"/"False" text with the new waypoint circle.
const LABEL_OFFSET = 14;
function labelPositionAlongPath(points) {
  const segmentLengths = points.slice(1).map((p, i) => Math.hypot(p.x - points[i].x, p.y - points[i].y));
  const totalLength = segmentLengths.reduce((sum, len) => sum + len, 0);
  let remaining = totalLength / 2;
  for (let i = 0; i < segmentLengths.length; i++) {
    const segLen = segmentLengths[i];
    if (i === segmentLengths.length - 1 || remaining <= segLen) {
      const t = segLen > 0 ? remaining / segLen : 0;
      const a = points[i];
      const b = points[i + 1];
      const dx = b.x - a.x;
      const dy = b.y - a.y;
      const len = Math.hypot(dx, dy) || 1;
      return {
        x: a.x + dx * t + (-dy / len) * LABEL_OFFSET,
        y: a.y + dy * t + (dx / len) * LABEL_OFFSET,
      };
    }
    remaining -= segLen;
  }
  return points[Math.floor(points.length / 2)]; // unreachable with 2+ points, but a safe fallback
}

// Registered as the 'default' edge type (see WorkflowBuilder's edgeTypes) so every edge in
// the builder gets this behavior without touching the many places edges get created. An edge
// with no waypoints still renders React Flow's normal bezier curve (so nothing changes
// visually until someone actually uses this); once a waypoint is added it switches to a
// smooth curve running source -> waypoints -> target (see smoothPath). Double-click the line to
// add a waypoint (also offered via right-click, see WorkflowBuilder's onEdgeContextMenu), drag
// a waypoint to move it, double-click OR right-click a waypoint to remove it.
function DraggableEdge({
  id,
  sourceX,
  sourceY,
  sourcePosition,
  targetX,
  targetY,
  targetPosition,
  style,
  markerEnd,
  label,
  data,
}) {
  // screenToFlowPosition is a pure coordinate-conversion helper, safe to read from the internal
  // store — but edge updates must go through WorkflowBuilder's own setEdges (see
  // WorkflowEdgesContext), not useReactFlow().setEdges. openMenu opens WorkflowBuilder's shared
  // right-click menu overlay (same one the multi-select align and edge menus use).
  const { screenToFlowPosition } = useReactFlow();
  const { setEdges, openMenu } = useContext(WorkflowEdgesContext) || {};
  const waypoints = useMemo(() => data?.waypoints || [], [data?.waypoints]);
  const hasWaypoints = waypoints.length > 0;

  const points = useMemo(
    () => [{ x: sourceX, y: sourceY }, ...waypoints, { x: targetX, y: targetY }],
    [sourceX, sourceY, targetX, targetY, waypoints]
  );
  const [bezierPath, bezierLabelX, bezierLabelY] = getBezierPath({
    sourceX,
    sourceY,
    sourcePosition,
    targetX,
    targetY,
    targetPosition,
  });
  const curvedPath = useMemo(() => smoothPath(points), [points]);
  const path = hasWaypoints ? curvedPath : bezierPath;
  const labelPoint = useMemo(
    () => (hasWaypoints ? labelPositionAlongPath(points) : { x: bezierLabelX, y: bezierLabelY }),
    [hasWaypoints, points, bezierLabelX, bezierLabelY]
  );

  const updateWaypoints = useCallback(
    (next) => {
      if (!setEdges) return;
      setEdges((eds) => eds.map((e) => (e.id === id ? { ...e, data: { ...e.data, waypoints: next } } : e)));
    },
    [id, setEdges]
  );

  const onPathDoubleClick = useCallback(
    (event) => {
      event.stopPropagation();
      const click = screenToFlowPosition({ x: event.clientX, y: event.clientY });
      // `points` is [source, ...waypoints, target], so segment i starts at waypoints[i - 1] —
      // inserting at the returned index in the waypoints array lands the new point in the same
      // spot in the segment list.
      const next = [...waypoints];
      next.splice(findInsertionIndex(points, click), 0, click);
      updateWaypoints(next);
    },
    [points, waypoints, screenToFlowPosition, updateWaypoints]
  );

  const onWaypointPointerDown = useCallback(
    (event, index) => {
      event.stopPropagation();
      event.preventDefault();
      const onMove = (moveEvent) => {
        const flowPos = screenToFlowPosition({ x: moveEvent.clientX, y: moveEvent.clientY });
        updateWaypoints(waypoints.map((wp, i) => (i === index ? flowPos : wp)));
      };
      const onUp = () => {
        window.removeEventListener('pointermove', onMove);
        window.removeEventListener('pointerup', onUp);
      };
      window.addEventListener('pointermove', onMove);
      window.addEventListener('pointerup', onUp);
    },
    [waypoints, screenToFlowPosition, updateWaypoints]
  );

  const removeWaypoint = useCallback(
    (index) => updateWaypoints(waypoints.filter((_, i) => i !== index)),
    [waypoints, updateWaypoints]
  );

  const onWaypointDoubleClick = useCallback(
    (event, index) => {
      event.stopPropagation();
      removeWaypoint(index);
    },
    [removeWaypoint]
  );

  const onWaypointContextMenu = useCallback(
    (event, index) => {
      event.preventDefault();
      event.stopPropagation();
      openMenu?.(event, 'Waypoint', [
        { key: 'delete-waypoint', label: 'Delete Waypoint', onClick: () => removeWaypoint(index) },
      ]);
    },
    [openMenu, removeWaypoint]
  );

  return (
    <>
      <BaseEdge id={id} path={path} style={style} markerEnd={markerEnd} />
      {/* Wide, invisible hit-target so double-clicking to add a waypoint doesn't require
          pixel-perfect accuracy on the (often thin) visible stroke. strokeOpacity (not
          stroke="transparent") keeps the path "painted" for pointer-event hit-testing purposes —
          matches the pattern React Flow's own BaseEdge interaction path uses. */}
      <path d={path} fill="none" stroke="#000" strokeOpacity={0} strokeWidth={16} onDoubleClick={onPathDoubleClick} />
      <EdgeLabelRenderer>
        {label && labelPoint && (
          <div
            className="workflow-edge-label nodrag nopan"
            style={{ transform: `translate(-50%, -50%) translate(${labelPoint.x}px, ${labelPoint.y}px)` }}
          >
            {label}
          </div>
        )}
        {waypoints.map((wp, i) => (
          <div
            key={i}
            className="workflow-edge-waypoint nodrag nopan"
            style={{ transform: `translate(-50%, -50%) translate(${wp.x}px, ${wp.y}px)` }}
            onPointerDown={(e) => onWaypointPointerDown(e, i)}
            onDoubleClick={(e) => onWaypointDoubleClick(e, i)}
            onContextMenu={(e) => onWaypointContextMenu(e, i)}
            title="Waypoint — drag to reroute, double-click or right-click to remove"
          />
        ))}
      </EdgeLabelRenderer>
    </>
  );
}

export default React.memo(DraggableEdge);
